// src/ryan/components/RyanSavedFoldersView.tsx

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    DndContext, closestCenter, PointerSensor,
    KeyboardSensor, useSensor, useSensors, DragEndEvent, DragOverlay
} from '@dnd-kit/core';
import {
    SortableContext, sortableKeyboardCoordinates,
    useSortable, rectSortingStrategy, arrayMove
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
    Folder, FolderHeart, FolderSearch, FolderClock, FolderKanban,
    Rocket, Zap, Sparkles, Coffee, Ghost, Star, Flame, Brain,
    Target, Trophy, Lightbulb, Music, Leaf, Shield, Crown,
    Compass, Archive, Tag, Pizza, Banana,
    Plus, GripVertical, Trash2, Edit2, ArrowLeft, BookOpen, Bookmark,
} from 'lucide-react';
import OrganizeModal from '../../components/study/OrganizeModal';
import '../../components/dashboard/SavedFoldersView.css';

const API_RYAN = '/api/ryan/saved';
const token = () => localStorage.getItem('accessToken');
const authHeader = () => ({ Authorization: `Bearer ${token()}` });

const ICON_MAP: Record<string, React.ElementType> = {
    Folder, FolderHeart, FolderSearch, FolderClock, FolderKanban,
    Rocket, Zap, Sparkles, Coffee, Ghost, Star, Flame, Brain,
    Target, Trophy, Lightbulb, Music, Leaf, Shield, Crown,
    Compass, Archive, Tag, Pizza, Banana,
};

const ICON_OPTIONS: { id: string; label: string }[] = [
    { id: 'Folder', label: 'Pasta' },
    { id: 'FolderHeart', label: 'Favoritos' },
    { id: 'FolderSearch', label: 'Pesquisa' },
    { id: 'FolderClock', label: 'Recentes' },
    { id: 'FolderKanban', label: 'Projeto' },
    { id: 'Rocket', label: 'Lançamento' },
    { id: 'Zap', label: 'Rápido' },
    { id: 'Sparkles', label: 'Destaque' },
    { id: 'Coffee', label: 'Casual' },
    { id: 'Ghost', label: 'Especial' },
    { id: 'Star', label: 'Estrela' },
    { id: 'Flame', label: 'Em fogo' },
    { id: 'Brain', label: 'Conhecimento' },
    { id: 'Target', label: 'Foco' },
    { id: 'Trophy', label: 'Conquista' },
    { id: 'Lightbulb', label: 'Ideias' },
    { id: 'Music', label: 'Música' },
    { id: 'Leaf', label: 'Natureza' },
    { id: 'Shield', label: 'Importante' },
    { id: 'Crown', label: 'Top' },
    { id: 'Compass', label: 'Explorar' },
    { id: 'Pizza', label: 'Descanso' },
    { id: 'Banana', label: 'Inusitado' },
];

export const getIcon = (id: string): React.ElementType => ICON_MAP[id] ?? Folder;

interface RyanFolder {
    _id: string;
    name: string;
    icon: string;
    color: string;
    cardCount: number;
    order: number;
}

interface RyanSavedCardFull {
    savedCardId: string;
    cardId: string;
    deckId: string;
    subdeckId: string;
    hierarchy: { spaceName: string; spaceId: string; subdeckName: string };
    savedAt: string;
    content?: {
        front: string;
        back: string;
        supportContent?: string;
        references?: string;
    };
}

interface RyanSavedFoldersViewProps {
    onShowToast: (message: string, type: 'success' | 'error' | 'info') => void;
    externalCreateTrigger?: boolean;
    onExternalCreateHandled?: () => void;
    initialView?: 'all-cards' | 'folder';
    initialFolderId?: string;
}

// ── Card item inside a folder (list view) ──
const FolderCardItem: React.FC<{
    card: RyanSavedCardFull;
    folderId: string;
    onRemoveFromFolder: (savedCardId: string, folderId: string) => void;
    onOrganize: (savedCardId: string) => void;
}> = ({ card, folderId, onRemoveFromFolder, onOrganize }) => {
    const [showBack, setShowBack] = useState(false);

    const stripHtml = (html: string) => {
        const tmp = document.createElement('div');
        tmp.innerHTML = html;
        return tmp.textContent || tmp.innerText || '';
    };

    return (
        <div className="folder-card-item" onClick={() => setShowBack(v => !v)}>
            <div className="folder-card-header">
                <div className="folder-card-hierarchy">
                    <span>{card.hierarchy.spaceName || 'Ryan'}</span>
                    <span className="sep">→</span>
                    <span>{card.hierarchy.subdeckName}</span>
                </div>
                <div className="folder-card-actions" onClick={e => e.stopPropagation()}>
                    <button className="folder-card-btn" onClick={() => onOrganize(card.savedCardId)} title="Mover para outra pasta">
                        <Folder size={13} />
                    </button>
                    <button className="folder-card-btn danger" onClick={() => onRemoveFromFolder(card.savedCardId, folderId)} title="Remover desta pasta">
                        <Trash2 size={13} />
                    </button>
                </div>
            </div>
            <div className={`folder-card-body ${showBack ? 'flipped' : ''}`}>
                <div className="folder-card-label">{showBack ? 'Resposta' : 'Pergunta'}</div>
                <div className="folder-card-text">
                    {stripHtml(showBack ? (card.content?.back || '') : (card.content?.front || ''))}
                </div>
            </div>
            <div className="folder-card-hint">Toque para virar</div>
        </div>
    );
};

// ── Card item in the 3-column "Todos" grid ──
const AllCardItem: React.FC<{
    card: RyanSavedCardFull;
    onOrganize: (savedCardId: string) => void;
}> = ({ card, onOrganize }) => {
    const [showBack, setShowBack] = useState(false);

    const stripHtml = (html: string) => {
        const tmp = document.createElement('div');
        tmp.innerHTML = html;
        return tmp.textContent || tmp.innerText || '';
    };

    const preview = stripHtml(showBack ? (card.content?.back || '') : (card.content?.front || ''));

    return (
        <div className="rsf-all-card-item" onClick={() => setShowBack(v => !v)}>
            <div className="rsf-all-card-hierarchy">
                <span>{card.hierarchy.spaceName || 'Ryan'}</span>
                <span className="rsf-sep">›</span>
                <span>{card.hierarchy.subdeckName}</span>
            </div>
            <div className="rsf-all-card-label">{showBack ? 'Resposta' : 'Pergunta'}</div>
            <div className="rsf-all-card-text">{preview || '(sem conteúdo)'}</div>
            <div className="rsf-all-card-footer" onClick={e => e.stopPropagation()}>
                <span className="rsf-all-card-hint">Toque para virar</span>
                <button className="rsf-all-card-organize-btn" onClick={() => onOrganize(card.savedCardId)}>
                    <Folder size={11} /> Pasta
                </button>
            </div>
        </div>
    );
};

// ── Drag overlay ghost card ──
const FolderDragOverlay: React.FC<{ folder: RyanFolder }> = ({ folder }) => {
    const IconComp = getIcon(folder.icon);
    return (
        <div className="rsf-drag-overlay">
            <div className="rsf-folder-icon" style={{ background: `${folder.color}14`, border: `1.5px solid ${folder.color}2a` }}>
                <IconComp size={30} strokeWidth={1.7} color={folder.color} />
            </div>
            <div className="rsf-folder-text">
                <span className="rsf-folder-name">{folder.name}</span>
                <span className="rsf-folder-count">{folder.cardCount} card{folder.cardCount !== 1 ? 's' : ''}</span>
            </div>
        </div>
    );
};

// ── Sortable folder card (2-column grid) ──
const SortableFolderCard: React.FC<{
    folder: RyanFolder;
    onOpen: () => void;
    onEdit: () => void;
    onDelete: () => void;
    onStudy: () => void;
}> = ({ folder, onOpen, onEdit, onDelete, onStudy }) => {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: folder._id });
    const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.08 : 1 };
    const IconComp = getIcon(folder.icon);

    return (
        <div ref={setNodeRef} style={style} className={`sfv-folder-card rsf-folder-card${isDragging ? ' dragging' : ''}`}>
            {/* Controls: drag + edit + delete — absolute top-right, appear on hover */}
            <div className="rsf-folder-controls" onClick={e => e.stopPropagation()}>
                <div className="rsf-drag-handle" {...attributes} {...listeners} title="Reordenar">
                    <GripVertical size={15} />
                </div>
                <button className="rsf-folder-btn rsf-btn-edit" onClick={onEdit} title="Renomear">
                    <Edit2 size={14} />
                </button>
                <button className="rsf-folder-btn rsf-btn-delete" onClick={onDelete} title="Deletar pasta">
                    <Trash2 size={14} />
                </button>
            </div>

            {/* Main: icon + name + count */}
            <div className="rsf-folder-main" onClick={onOpen}>
                <div className="rsf-folder-icon" style={{ background: `${folder.color}14`, border: `1.5px solid ${folder.color}2a` }}>
                    <IconComp size={30} strokeWidth={1.7} color={folder.color} />
                </div>
                <div className="rsf-folder-text">
                    <span className="rsf-folder-name">{folder.name}</span>
                    <span className="rsf-folder-count">{folder.cardCount} card{folder.cardCount !== 1 ? 's' : ''}</span>
                </div>
            </div>

            {/* Footer: study + open */}
            <div className="rsf-folder-footer">
                <button className="rsf-study-btn" onClick={e => { e.stopPropagation(); onStudy(); }}>
                    <BookOpen size={12} /> Estudar
                </button>
                <button className="rsf-open-btn" onClick={e => { e.stopPropagation(); onOpen(); }}>
                    Ver cards
                </button>
            </div>
        </div>
    );
};

// ── Componente principal ──
const RyanSavedFoldersView: React.FC<RyanSavedFoldersViewProps> = ({
    onShowToast,
    externalCreateTrigger,
    onExternalCreateHandled,
    initialView,
    initialFolderId,
}) => {
    const navigate = useNavigate();

    const [folders, setFolders] = useState<RyanFolder[]>([]);
    const [totalCards, setTotalCards] = useState(0);
    const [isLoading, setIsLoading] = useState(true);

    const [openFolder, setOpenFolder] = useState<RyanFolder | null>(null);
    const [folderCards, setFolderCards] = useState<RyanSavedCardFull[]>([]);
    const [isLoadingCards, setIsLoadingCards] = useState(false);

    const [showAllCards, setShowAllCards] = useState(initialView === 'all-cards');
    const [allCards, setAllCards] = useState<RyanSavedCardFull[]>([]);
    const [isLoadingAllCards, setIsLoadingAllCards] = useState(false);

    const [activeDragId, setActiveDragId] = useState<string | null>(null);

    const [organizeCardId, setOrganizeCardId] = useState<string | null>(null);
    const [showOrganizeModal, setShowOrganizeModal] = useState(false);

    const [showCreateDialog, setShowCreateDialog] = useState(false);
    const [showEditDialog, setShowEditDialog] = useState(false);
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const [selectedFolder, setSelectedFolder] = useState<RyanFolder | null>(null);
    const [folderName, setFolderName] = useState('');
    const [folderIcon, setFolderIcon] = useState('Folder');

    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
        useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
    );

    // Trigger from page header "Nova Pasta" button
    useEffect(() => {
        if (externalCreateTrigger) {
            setFolderName('');
            setFolderIcon('Folder');
            setShowCreateDialog(true);
            onExternalCreateHandled?.();
        }
    }, [externalCreateTrigger, onExternalCreateHandled]);

    const fetchFolders = useCallback(async () => {
        setIsLoading(true);
        try {
            const res = await fetch(`${API_RYAN}/folders`, { headers: authHeader() });
            if (res.ok) {
                const data = await res.json();
                setFolders(data.folders || []);
                setTotalCards(data.totalCards || 0);
            }
        } finally {
            setIsLoading(false);
        }
    }, []);

    const fetchFolderCards = useCallback(async (folderId: string) => {
        setIsLoadingCards(true);
        try {
            const res = await fetch(`${API_RYAN}/folders/${folderId}/cards`, { headers: authHeader() });
            if (res.ok) {
                const data = await res.json();
                setFolderCards(data.cards || []);
            }
        } catch { onShowToast('Erro ao carregar cards', 'error'); }
        finally { setIsLoadingCards(false); }
    }, [onShowToast]);

    const fetchAllCards = useCallback(async () => {
        setIsLoadingAllCards(true);
        try {
            const res = await fetch(`${API_RYAN}/all-content`, { headers: authHeader() });
            if (res.ok) {
                const data = await res.json();
                setAllCards(data.cards || []);
            }
        } catch { onShowToast('Erro ao carregar cards', 'error'); }
        finally { setIsLoadingAllCards(false); }
    }, [onShowToast]);

    useEffect(() => { fetchFolders(); }, [fetchFolders]);

    useEffect(() => {
        if (initialView === 'all-cards') fetchAllCards();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (initialView === 'folder' && initialFolderId && folders.length > 0 && !openFolder) {
            const f = folders.find(folder => folder._id === initialFolderId);
            if (f) { setOpenFolder(f); fetchFolderCards(f._id); }
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [folders]);

    const handleOpenFolder = (folder: RyanFolder) => {
        setOpenFolder(folder);
        fetchFolderCards(folder._id);
    };

    const handleOpenAllCards = () => {
        setShowAllCards(true);
        fetchAllCards();
    };

    const handleBack = () => {
        if (openFolder) {
            setOpenFolder(null);
            setFolderCards([]);
            fetchFolders();
        } else {
            setShowAllCards(false);
        }
    };

    const handleStudyFolder = async (folderId: string) => {
        try {
            const res = await fetch(`${API_RYAN}/study/${folderId}`, { headers: authHeader() });
            if (!res.ok) { onShowToast('Erro ao preparar estudo', 'error'); return; }
            const data = await res.json();
            if (!data.pool || data.pool.length === 0) {
                onShowToast('Nenhum card para estudar nesta pasta', 'info');
                return;
            }
            localStorage.setItem('customStudyPool', JSON.stringify(data.pool));
            sessionStorage.setItem('graia_theme_context', 'ryan');
            navigate('/study/custom', {
                state: { fromRyan: true, returnPath: '/ryan', ryanFolderId: folderId, ryanReturnMode: 'saved', ryanReturnView: 'folder', ryanReturnFolderId: folderId }
            });
        } catch {
            onShowToast('Erro ao iniciar estudo', 'error');
        }
    };

    const handleStudyAllCards = async () => {
        try {
            const res = await fetch(`${API_RYAN}/study-all`, { headers: authHeader() });
            if (!res.ok) { onShowToast('Erro ao preparar estudo', 'error'); return; }
            const data = await res.json();
            if (!data.pool || data.pool.length === 0) {
                onShowToast('Nenhum card salvo para estudar', 'info');
                return;
            }
            localStorage.setItem('customStudyPool', JSON.stringify(data.pool));
            sessionStorage.setItem('graia_theme_context', 'ryan');
            navigate('/study/custom', {
                state: { fromRyan: true, returnPath: '/ryan', ryanReturnMode: 'saved', ryanReturnView: 'all-cards' }
            });
        } catch {
            onShowToast('Erro ao iniciar estudo', 'error');
        }
    };

    const handleRemoveFromFolder = async (savedCardId: string, folderId: string) => {
        try {
            const res = await fetch(`${API_RYAN}/folders/${folderId}/cards/${savedCardId}`, {
                method: 'DELETE', headers: authHeader()
            });
            if (res.ok) {
                setFolderCards(prev => prev.filter(c => c.savedCardId !== savedCardId));
                setFolders(prev => prev.map(f =>
                    f._id === folderId ? { ...f, cardCount: Math.max(0, f.cardCount - 1) } : f
                ));
                onShowToast('Removido da pasta', 'info');
            }
        } catch { onShowToast('Erro ao remover', 'error'); }
    };

    const handleCreateFolder = async () => {
        if (!folderName.trim()) { onShowToast('Digite um nome', 'error'); return; }
        try {
            const res = await fetch(`${API_RYAN}/folders`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', ...authHeader() },
                body: JSON.stringify({ name: folderName.trim(), icon: folderIcon })
            });
            if (res.ok) {
                setShowCreateDialog(false); setFolderName(''); setFolderIcon('Folder');
                fetchFolders(); onShowToast('Pasta criada!', 'success');
            } else {
                const err = await res.json();
                onShowToast(err.message || 'Erro ao criar', 'error');
            }
        } catch { onShowToast('Erro ao criar pasta', 'error'); }
    };

    const handleEditFolder = async () => {
        if (!selectedFolder || !folderName.trim()) { onShowToast('Digite um nome', 'error'); return; }
        try {
            const res = await fetch(`${API_RYAN}/folders/${selectedFolder._id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json', ...authHeader() },
                body: JSON.stringify({ name: folderName.trim(), icon: folderIcon })
            });
            if (res.ok) {
                setShowEditDialog(false); setSelectedFolder(null);
                fetchFolders(); onShowToast('Pasta atualizada!', 'success');
            }
        } catch { onShowToast('Erro ao editar pasta', 'error'); }
    };

    const handleDeleteFolder = async () => {
        if (!selectedFolder) return;
        try {
            const res = await fetch(`${API_RYAN}/folders/${selectedFolder._id}`, {
                method: 'DELETE', headers: authHeader()
            });
            if (res.ok) {
                setShowDeleteDialog(false); setSelectedFolder(null);
                fetchFolders(); onShowToast('Pasta deletada. Cards continuam salvos.', 'info');
            }
        } catch { onShowToast('Erro ao deletar', 'error'); }
    };

    const handleDragEnd = async (event: DragEndEvent) => {
        setActiveDragId(null);
        const { active, over } = event;
        if (!over || active.id === over.id) return;
        const oldIndex = folders.findIndex(f => f._id === active.id);
        const newIndex = folders.findIndex(f => f._id === over.id);
        const newFolders = arrayMove(folders, oldIndex, newIndex);
        setFolders(newFolders);
        try {
            await fetch(`${API_RYAN}/folders/reorder/batch`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json', ...authHeader() },
                body: JSON.stringify({ order: newFolders.map((f, i) => ({ folderId: f._id, order: i })) })
            });
        } catch { onShowToast('Erro ao reordenar', 'error'); }
    };

    const handleOrganize = (savedCardId: string) => {
        setOrganizeCardId(savedCardId);
        setShowOrganizeModal(true);
    };

    const IconPicker = () => (
        <div className="sfv-icon-picker">
            {ICON_OPTIONS.map(({ id, label }) => {
                const Icon = getIcon(id);
                return (
                    <button
                        key={id}
                        className={`sfv-icon-btn ${folderIcon === id ? 'active' : ''}`}
                        onClick={() => setFolderIcon(id)}
                        title={label}
                        type="button"
                    >
                        <Icon size={17} strokeWidth={1.8} />
                    </button>
                );
            })}
        </div>
    );

    // ── Vista: todos os flashcards (3 colunas) ──
    if (showAllCards) {
        return (
            <div className="sfv-container">
                <div className="sfv-breadcrumb">
                    <button className="sfv-back-btn" onClick={handleBack}>
                        <ArrowLeft size={16} />Pastas
                    </button>
                    <span className="sfv-breadcrumb-sep">›</span>
                    <span className="sfv-breadcrumb-current">
                        <Archive size={15} strokeWidth={1.8} />
                        Todos os flashcards
                    </span>
                    {allCards.length > 0 && (
                        <button className="sfv-study-btn" onClick={handleStudyAllCards}>
                            <BookOpen size={14} />Estudar todos
                        </button>
                    )}
                </div>

                {isLoadingAllCards ? (
                    <div className="sfv-loading"><span className="sfv-spinner" /></div>
                ) : allCards.length === 0 ? (
                    <div className="sfv-empty">
                        <Bookmark size={36} strokeWidth={1.5} />
                        <p>Nenhum flashcard salvo ainda</p>
                    </div>
                ) : (
                    <div className="rsf-all-cards-grid">
                        {allCards.map(card => (
                            <AllCardItem
                                key={card.savedCardId}
                                card={card}
                                onOrganize={handleOrganize}
                            />
                        ))}
                    </div>
                )}

                <OrganizeModal
                    isOpen={showOrganizeModal}
                    savedCardId={organizeCardId}
                    onClose={() => { setShowOrganizeModal(false); setOrganizeCardId(null); }}
                    onShowToast={onShowToast}
                    fromRyan={true}
                />
            </div>
        );
    }

    // ── Vista: cards de uma pasta (lista) ──
    if (openFolder) {
        const FolderIconComp = getIcon(openFolder.icon);

        return (
            <div className="sfv-container">
                <div className="sfv-breadcrumb">
                    <button className="sfv-back-btn" onClick={handleBack}>
                        <ArrowLeft size={16} />Pastas
                    </button>
                    <span className="sfv-breadcrumb-sep">›</span>
                    <span className="sfv-breadcrumb-current">
                        <FolderIconComp size={15} strokeWidth={1.8} />
                        {openFolder.name}
                    </span>
                    {folderCards.length > 0 && (
                        <button className="sfv-study-btn" onClick={() => handleStudyFolder(openFolder._id)}>
                            <BookOpen size={14} />Estudar pasta
                        </button>
                    )}
                </div>

                {isLoadingCards ? (
                    <div className="sfv-loading"><span className="sfv-spinner" /></div>
                ) : folderCards.length === 0 ? (
                    <div className="sfv-empty">
                        <Bookmark size={36} strokeWidth={1.5} />
                        <p>Nenhum flashcard nesta pasta</p>
                    </div>
                ) : (
                    <div className="sfv-cards-grid">
                        {folderCards.map(card => (
                            <FolderCardItem
                                key={card.savedCardId}
                                card={card}
                                folderId={openFolder._id}
                                onRemoveFromFolder={handleRemoveFromFolder}
                                onOrganize={handleOrganize}
                            />
                        ))}
                    </div>
                )}

                <OrganizeModal
                    isOpen={showOrganizeModal}
                    savedCardId={organizeCardId}
                    onClose={() => { setShowOrganizeModal(false); setOrganizeCardId(null); }}
                    onShowToast={onShowToast}
                    fromRyan={true}
                />
            </div>
        );
    }

    // ── Grid de pastas (2 colunas) ──
    return (
        <div className="sfv-container">
            {isLoading ? (
                <div className="sfv-loading"><span className="sfv-spinner" /></div>
            ) : (
                <>
                    <div className="sfv-all-card">
                        <div className="sfv-all-card-info" onClick={handleOpenAllCards}>
                            <div className="sfv-all-icon">
                                <Archive size={26} strokeWidth={1.6} color="#ffffff" />
                            </div>
                            <div className="sfv-all-info">
                                <span className="sfv-all-name">Todos os flashcards</span>
                                <span className="sfv-all-count">{totalCards} salvo{totalCards !== 1 ? 's' : ''}</span>
                            </div>
                        </div>
                        <div className="rsf-all-entry-btns">
                            {totalCards > 0 && (
                                <button className="rsf-all-study-entry-btn" onClick={handleStudyAllCards}>
                                    <BookOpen size={13} /> Estudar
                                </button>
                            )}
                            <button className="rsf-all-view-btn" onClick={handleOpenAllCards}>
                                Ver cards
                            </button>
                        </div>
                    </div>

                    {folders.length === 0 ? (
                        <div className="sfv-empty-folders">
                            <Folder size={40} strokeWidth={1.2} />
                            <p>Nenhuma pasta criada</p>
                            <span>Organize seus flashcards em pastas para estudar com mais foco</span>
                            <button className="sfv-btn-create-empty" onClick={() => { setFolderName(''); setFolderIcon('Folder'); setShowCreateDialog(true); }}>
                                <Plus size={15} />Criar primeira pasta
                            </button>
                        </div>
                    ) : (
                        <DndContext
                            sensors={sensors}
                            collisionDetection={closestCenter}
                            onDragStart={e => setActiveDragId(e.active.id as string)}
                            onDragEnd={handleDragEnd}
                        >
                            <SortableContext items={folders.map(f => f._id)} strategy={rectSortingStrategy}>
                                <div className="sfv-folders-grid">
                                    {folders.map(folder => (
                                        <SortableFolderCard
                                            key={folder._id}
                                            folder={folder}
                                            onOpen={() => handleOpenFolder(folder)}
                                            onEdit={() => { setSelectedFolder(folder); setFolderName(folder.name); setFolderIcon(folder.icon); setShowEditDialog(true); }}
                                            onDelete={() => { setSelectedFolder(folder); setShowDeleteDialog(true); }}
                                            onStudy={() => handleStudyFolder(folder._id)}
                                        />
                                    ))}
                                </div>
                            </SortableContext>
                            <DragOverlay>
                                {activeDragId ? (
                                    <FolderDragOverlay folder={folders.find(f => f._id === activeDragId)!} />
                                ) : null}
                            </DragOverlay>
                        </DndContext>
                    )}
                </>
            )}

            {showCreateDialog && (
                <div className="sfv-dialog-overlay" onClick={() => setShowCreateDialog(false)}>
                    <div className="sfv-dialog" onClick={e => e.stopPropagation()}>
                        <h3>Nova pasta</h3>
                        <p className="sfv-dialog-sublabel">Escolha um ícone</p>
                        <IconPicker />
                        <input type="text" placeholder="Nome da pasta" value={folderName} onChange={e => setFolderName(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleCreateFolder()} autoFocus maxLength={60} />
                        <div className="sfv-dialog-actions">
                            <button onClick={() => setShowCreateDialog(false)}>Cancelar</button>
                            <button className="btn-primary" onClick={handleCreateFolder}>Criar</button>
                        </div>
                    </div>
                </div>
            )}

            {showEditDialog && selectedFolder && (
                <div className="sfv-dialog-overlay" onClick={() => setShowEditDialog(false)}>
                    <div className="sfv-dialog" onClick={e => e.stopPropagation()}>
                        <h3>Editar pasta</h3>
                        <p className="sfv-dialog-sublabel">Escolha um ícone</p>
                        <IconPicker />
                        <input type="text" placeholder="Nome da pasta" value={folderName} onChange={e => setFolderName(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleEditFolder()} autoFocus maxLength={60} />
                        <div className="sfv-dialog-actions">
                            <button onClick={() => setShowEditDialog(false)}>Cancelar</button>
                            <button className="btn-primary" onClick={handleEditFolder}>Salvar</button>
                        </div>
                    </div>
                </div>
            )}

            {showDeleteDialog && selectedFolder && (
                <div className="sfv-dialog-overlay" onClick={() => setShowDeleteDialog(false)}>
                    <div className="sfv-dialog" onClick={e => e.stopPropagation()}>
                        <h3>Deletar pasta</h3>
                        <p>Deletar <strong>"{selectedFolder.name}"</strong>?</p>
                        <p className="sfv-dialog-note">Os flashcards continuarão salvos — apenas a pasta será removida.</p>
                        <div className="sfv-dialog-actions">
                            <button onClick={() => setShowDeleteDialog(false)}>Cancelar</button>
                            <button className="btn-danger" onClick={handleDeleteFolder}>Deletar</button>
                        </div>
                    </div>
                </div>
            )}

            <OrganizeModal
                isOpen={showOrganizeModal}
                savedCardId={organizeCardId}
                onClose={() => { setShowOrganizeModal(false); setOrganizeCardId(null); }}
                onShowToast={onShowToast}
                fromRyan={true}
            />
        </div>
    );
};

export default RyanSavedFoldersView;
